import { useState, useEffect, useRef } from "react";
import { axiosInstance } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, Download, Trash2, FileText, Search, Edit, CheckSquare, Square, RefreshCw, Link, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { ensureCsvPassword } from "@/lib/csvPassword";

export default function Invoices({ user, onLogout }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState([]);
  const [importId, setImportId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedPartner, setSelectedPartner] = useState("");
  const [selectedInstallation, setSelectedInstallation] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedCommissionStatus, setSelectedCommissionStatus] = useState("");
  const [dueDateStart, setDueDateStart] = useState("");
  const [dueDateEnd, setDueDateEnd] = useState("");
  const [partners, setPartners] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const fileInputRef = useRef(null);
  const updateFileInputRef = useRef(null);
  
  // Estado para ordenação
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' ou 'desc'
  
  // Estado para edição
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  
  // Estado para seleção múltipla
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkActionType, setBulkActionType] = useState(null); // 'payment', 'commission', 'bonus'
  const [bulkActionStatus, setBulkActionStatus] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);
  
  // Última importação CSV (admin only)
  const [lastImport, setLastImport] = useState(null);
  
  // Estado para recalcular bônus
  const [recalculatingBonus, setRecalculatingBonus] = useState(false);
  
  // Estado para recalcular comissões
  const [recalculatingCommissions, setRecalculatingCommissions] = useState(false);
  
  // Estado para edição inline de bônus
  const [editingBonusId, setEditingBonusId] = useState(null);
  const [editingBonusValue, setEditingBonusValue] = useState('');
  
  // Estado para edição inline de comissão e valor da fatura
  const [editingField, setEditingField] = useState({ id: null, field: null, value: '' });
  
  // Helper function to check if user can edit
  const canEdit = () => {
    return user?.role === 'admin' || user?.role === 'operador';
  };

  // Salvar edição inline de commission_amount / invoice_amount
  const handleSaveInlineField = async (invoiceId, field) => {
    try {
      const raw = String(editingField.value || '').replace(/\./g, '').replace(',', '.');
      const num = parseFloat(raw);
      if (isNaN(num) || num < 0) {
        toast.error("Valor inválido");
        return;
      }
      await axiosInstance.put(`/invoices/${invoiceId}`, { [field]: num });
      toast.success(field === 'commission_amount' ? "Comissão atualizada!" : "Valor da fatura atualizado!");
      setEditingField({ id: null, field: null, value: '' });
      fetchInvoices();
    } catch (error) {
      console.error(`Erro ao atualizar ${field}:`, error);
      toast.error(error.response?.data?.detail || `Erro ao atualizar valor`);
    }
  };

  const startEditingField = (invoice, field) => {
    setEditingField({ id: invoice.id, field, value: (invoice[field] || 0).toString() });
  };

  const cancelEditingField = () => {
    setEditingField({ id: null, field: null, value: '' });
  };

  // Função para salvar bônus inline
  const handleSaveBonusInline = async (invoiceId) => {
    try {
      const newBonus = parseFloat(editingBonusValue) || 0;
      await axiosInstance.put(`/invoices/${invoiceId}`, { bonus: newBonus });
      toast.success("Bônus atualizado!");
      setEditingBonusId(null);
      setEditingBonusValue('');
      fetchInvoices();
    } catch (error) {
      console.error("Erro ao atualizar bônus:", error);
      toast.error(error.response?.data?.detail || "Erro ao atualizar bônus");
    }
  };

  // Função para iniciar edição inline de bônus
  const startEditingBonus = (invoice) => {
    setEditingBonusId(invoice.id);
    setEditingBonusValue(invoice.bonus?.toString() || '0');
  };

  // Função para cancelar edição inline de bônus
  const cancelEditingBonus = () => {
    setEditingBonusId(null);
    setEditingBonusValue('');
  };

  // Função para ordenação
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Se já está ordenando por esta coluna, inverte a direção
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nova coluna, começa com ascendente
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Volta para primeira página ao ordenar
  };

  // Função para obter ícone de ordenação
  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3 h-3 ml-1 text-blue-600" />
      : <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
  };

  // Componente de cabeçalho ordenável
  const SortableHeader = ({ column, children, className = "" }) => (
    <TableHead 
      className={`cursor-pointer hover:bg-gray-100 select-none ${className}`}
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center">
        {children}
        {getSortIcon(column)}
      </div>
    </TableHead>
  );

  // Função para recalcular bônus de todas as faturas
  const handleRecalculateBonus = async () => {
    if (!window.confirm('Deseja recalcular o bônus de todas as faturas?\n\nIsso irá:\n- Aplicar bônus APENAS na primeira fatura de cada unidade consumidora\n- Remover bônus das demais faturas\n- Considerar apenas faturas com data de assinatura >= 18/09/2025')) {
      return;
    }
    
    try {
      setRecalculatingBonus(true);
      const response = await axiosInstance.post("/invoices/recalculate-all-bonus");
      
      toast.success(
        `Bônus recalculado!\n` +
        `Instalações processadas: ${response.data.installations_processed}\n` +
        `Bônus corrigidos: ${response.data.bonus_corrected}\n` +
        `Bônus removidos: ${response.data.bonus_removed}\n` +
        `Bônus mantidos: ${response.data.bonus_kept}`
      );
      
      // Recarregar faturas
      await fetchInvoices();
    } catch (error) {
      console.error("Erro ao recalcular bônus:", error);
      toast.error(error.response?.data?.detail || "Erro ao recalcular bônus");
    } finally {
      setRecalculatingBonus(false);
    }
  };

  // Função para recalcular comissões de todas as faturas
  const handleRecalculateCommissions = async () => {
    if (!window.confirm('Deseja recalcular as comissões de todas as faturas?\n\nIsso irá:\n- Atualizar % de comissão com base no cadastro atual do parceiro\n- Recalcular valor da comissão (incluindo bônus)\n- Regenerar registros de comissão')) {
      return;
    }
    
    try {
      setRecalculatingCommissions(true);
      const response = await axiosInstance.post("/invoices/recalculate-all-commissions");
      
      toast.success(
        `Comissões recalculadas!\n` +
        `Faturas atualizadas: ${response.data.invoices_updated}\n` +
        `Comissões regeneradas: ${response.data.commissions_regenerated}\n` +
        `Ignoradas: ${response.data.skipped}\n` +
        `Erros: ${response.data.errors}`
      );
      
      // Recarregar faturas
      await fetchInvoices();
    } catch (error) {
      console.error("Erro ao recalcular comissões:", error);
      toast.error(error.response?.data?.detail || "Erro ao recalcular comissões");
    } finally {
      setRecalculatingCommissions(false);
    }
  };

  // Estado para sincronização de parceiros
  const [syncingPartners, setSyncingPartners] = useState(false);

  // Função para sincronizar faturas sem parceiro
  const handleSyncPartners = async () => {
    if (!window.confirm('Deseja sincronizar automaticamente as faturas sem parceiro?\n\nIsso irá:\n- Buscar faturas que não têm parceiro vinculado\n- Tentar encontrar o parceiro correto pelo nome do cooperado\n- Vincular automaticamente (ignorando maiúsculas/minúsculas)')) {
      return;
    }
    
    try {
      setSyncingPartners(true);
      const response = await axiosInstance.post("/invoices/sync-partners");
      
      toast.success(
        `Sincronização concluída!\n` +
        `Faturas processadas: ${response.data.processed}\n` +
        `Parceiros vinculados: ${response.data.linked}\n` +
        `Não encontrados: ${response.data.not_found}`
      );
      
      // Recarregar faturas
      await fetchInvoices();
    } catch (error) {
      console.error("Erro ao sincronizar parceiros:", error);
      toast.error(error.response?.data?.detail || "Erro ao sincronizar parceiros");
    } finally {
      setSyncingPartners(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchPartners();
      fetchLastImport();
    }
  }, [user?.role]);

  // Busca com debounce (evita 1 requisição por tecla)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Filtros/ordenação/paginação no servidor: recarrega quando qualquer parâmetro muda
  useEffect(() => {
    if (!user?.role) return;
    fetchInvoices();
  }, [user?.role, currentPage, itemsPerPage, debouncedSearch, selectedMonth, selectedPartner, selectedInstallation,
      selectedStatus, selectedCommissionStatus, dueDateStart, dueDateEnd, sortColumn, sortDirection]);

  // Volta à página 1 ao mudar filtros/ordenação
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedMonth, selectedPartner, selectedInstallation, selectedStatus, selectedCommissionStatus,
      dueDateStart, dueDateEnd, sortColumn, sortDirection, itemsPerPage]);

  const fetchLastImport = async () => {
    try {
      const res = await axiosInstance.get("/invoices/last-csv-import");
      setLastImport(res.data);
    } catch (e) {
      console.warn("Falha ao carregar última importação CSV:", e?.message || e);
      // sem toast para não distrair — o banner apenas não é exibido
    }
  };

  const fetchPartners = async () => {
    try {
      const response = await axiosInstance.get("/partners");
      setPartners(response.data);
    } catch (error) {
      console.error("Erro ao carregar parceiros");
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, page_size: itemsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (selectedPartner) params.partner = selectedPartner;
      if (selectedInstallation) params.installation = selectedInstallation;
      if (selectedMonth) params.month = selectedMonth;
      if (selectedStatus) params.payment_status = selectedStatus;
      if (selectedCommissionStatus) params.commission_status = selectedCommissionStatus;
      if (dueDateStart) params.due_start = dueDateStart;
      if (dueDateEnd) params.due_end = dueDateEnd;
      if (sortColumn) { params.sort = sortColumn; params.dir = sortDirection; }
      const response = await axiosInstance.get("/invoices/page", { params });
      setInvoices(response.data.items || []);
      setTotalCount(response.data.total || 0);
      setAvailableMonths(response.data.months || []);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      toast.error("Erro ao carregar faturas");
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  };

  const handleImportCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setImporting(true);
      setImportErrors([]);
      setImportId(null);
      const response = await axiosInstance.post("/invoices/upload-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(response.data.message);
      if (response.data.errors && response.data.errors.length > 0) {
        setImportErrors(response.data.errors);
        setImportId(response.data.import_id);
        toast.warning(`${response.data.errors.length} erros encontrados. Clique em "Baixar Erros" para ver detalhes.`);
      }
      
      fetchInvoices();
      if (user?.role === 'admin') fetchLastImport();
      e.target.value = "";
    } catch (error) {
      console.error("Error importing CSV:", error);
      toast.error(error.response?.data?.detail || "Erro ao importar CSV");
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadErrors = async () => {
    if (!importId) return;

    try {
      const response = await axiosInstance.get(`/invoices/export-import-errors/${importId}`, {
        responseType: "blob"
      });

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `erros_importacao_faturas_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Erros exportados com sucesso");
    } catch (error) {
      console.error("Error downloading errors:", error);
      toast.error("Erro ao baixar arquivo de erros");
    }
  };

  const handleUpdateCSV = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setImporting(true);
      setImportErrors([]);
      setImportId(null);
      const response = await axiosInstance.post("/invoices/update-csv", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(response.data.message);
      if (response.data.errors > 0) {
        setImportId(response.data.import_id);
        toast.warning(`${response.data.errors} erro(s) encontrado(s). Clique em "Baixar Erros" para ver detalhes.`);
      }
      
      fetchInvoices();
      e.target.value = "";
    } catch (error) {
      console.error("Error updating invoices via CSV:", error);
      toast.error(error.response?.data?.detail || "Erro ao atualizar faturas via CSV");
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadUpdateErrors = async () => {
    if (!importId) return;

    try {
      const response = await axiosInstance.get(`/invoices/export-update-errors/${importId}`, {
        responseType: "blob"
      });

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `erros_atualizacao_faturas_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Erros exportados com sucesso");
    } catch (error) {
      console.error("Error downloading update errors:", error);
      toast.error("Erro ao baixar arquivo de erros");
    }
  };

  const handleDownloadUpdateTemplate = async () => {
    try {
      const response = await axiosInstance.get("/invoices/update-template", {
        responseType: "blob"
      });

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "modelo_atualizacao_faturas.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Modelo baixado com sucesso");
    } catch (error) {
      console.error("Error downloading update template:", error);
      toast.error("Erro ao baixar modelo");
    }
  };

  const handleExportCSV = async () => {
    if (!ensureCsvPassword()) return;
    try {
      // Build query params with current filters
      const params = {};
      if (selectedPartner) params.partner_name = selectedPartner;
      if (selectedInstallation) params.installation_number = selectedInstallation;
      if (selectedMonth) params.reference_month = selectedMonth;
      
      const response = await axiosInstance.get("/invoices/export-csv", {
        responseType: "blob",
        params
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "faturas.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("CSV exportado com sucesso!");
    } catch (error) {
      toast.error("Erro ao exportar CSV");
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get("/invoices/template", {
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "modelo_faturas.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Modelo baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar modelo");
    }
  };

  const handleUpdateCommissionStatus = async (invoiceId, status) => {
    try {
      await axiosInstance.put(`/invoices/${invoiceId}/commission-status?status=${status}`);
      toast.success("Status da comissão atualizado");
      fetchInvoices();
    } catch (error) {
      console.error("Error updating commission status:", error);
      toast.error("Erro ao atualizar status da comissão");
    }
  };

  const handleUpdateBonusStatus = async (invoiceId, status) => {
    try {
      await axiosInstance.put(`/invoices/${invoiceId}/bonus-status?status=${status}`);
      toast.success("Status do bônus atualizado");
      fetchInvoices();
    } catch (error) {
      console.error("Error updating bonus status:", error);
      toast.error("Erro ao atualizar status do bônus");
    }
  };

  const handleUpdatePaymentStatus = async (invoiceId, status) => {
    try {
      await axiosInstance.put(`/invoices/${invoiceId}/payment-status?status=${status}`);
      toast.success("Status de pagamento atualizado");
      fetchInvoices();
    } catch (error) {
      console.error("Error updating payment status:", error);
      toast.error("Erro ao atualizar status de pagamento");
    }
  };


  const handleDelete = async (invoiceId) => {
    if (!window.confirm("Tem certeza que deseja deletar esta fatura?")) return;

    try {
      await axiosInstance.delete(`/invoices/${invoiceId}`);
      toast.success("Fatura deletada com sucesso");
      fetchInvoices();
    } catch (error) {
      console.error("Error deleting invoice:", error);
      toast.error("Erro ao deletar fatura");
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axiosInstance.put(`/invoices/${editingInvoice.id}`, editFormData);
      toast.success("Fatura atualizada com sucesso!");
      setEditDialogOpen(false);
      setEditingInvoice(null);
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar fatura");
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`⚠️ ATENÇÃO: Deseja deletar TODAS as ${totalCount} faturas?\n\nEsta ação NÃO pode ser desfeita!`)) return;
    
    if (!window.confirm("Confirme novamente: Deletar TODAS as faturas?")) return;

    try {
      const response = await axiosInstance.delete("/invoices");
      toast.success(response.data.message);
      fetchInvoices();
    } catch (error) {
      console.error("Error deleting all invoices:", error);
      toast.error("Erro ao deletar faturas");
    }
  };

  // Funções para seleção múltipla
  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (pageInvoices) => {
    // Seleciona/deseleciona apenas os itens da página atual
    const pageIds = pageInvoices.map(inv => inv.id);
    const allPageSelected = pageIds.every(id => selectedInvoices.has(id));
    
    if (allPageSelected) {
      // Deselecionar apenas os da página atual
      setSelectedInvoices(prev => {
        const newSet = new Set(prev);
        pageIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } else {
      // Selecionar os da página atual (adicionar aos já selecionados)
      setSelectedInvoices(prev => {
        const newSet = new Set(prev);
        pageIds.forEach(id => newSet.add(id));
        return newSet;
      });
    }
  };

  const openBulkActionDialog = (actionType) => {
    setBulkActionType(actionType);
    setBulkActionStatus('');
    setBulkActionDialogOpen(true);
  };

  const handleBulkUpdate = async () => {
    if (!bulkActionStatus || selectedInvoices.size === 0) return;
    
    setBulkUpdating(true);
    try {
      const invoiceIds = Array.from(selectedInvoices);
      const response = await axiosInstance.put("/invoices/bulk-update", {
        invoice_ids: invoiceIds,
        action_type: bulkActionType,
        status: bulkActionStatus
      });
      
      toast.success(response.data.message);
      setBulkActionDialogOpen(false);
      setSelectedInvoices(new Set());
      fetchInvoices();
    } catch (error) {
      console.error("Error updating invoices:", error);
      toast.error(error.response?.data?.detail || "Erro ao atualizar faturas");
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedInvoices.size === 0) return;
    
    if (!window.confirm(`⚠️ ATENÇÃO: Deseja deletar ${selectedInvoices.size} fatura(s) selecionada(s)?\n\nEsta ação NÃO pode ser desfeita!`)) return;
    
    try {
      const invoiceIds = Array.from(selectedInvoices);
      const response = await axiosInstance.post("/invoices/bulk-delete", {
        invoice_ids: invoiceIds
      });
      
      toast.success(response.data.message);
      setSelectedInvoices(new Set());
      fetchInvoices();
    } catch (error) {
      console.error("Error deleting invoices:", error);
      toast.error(error.response?.data?.detail || "Erro ao deletar faturas");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString('pt-BR');
  };

  // Normaliza commission_status e bonus_status que podem estar em PT-BR no banco (legado)
  const normalizeStatus = (raw) => {
    const v = (raw == null ? '' : String(raw)).trim().toLowerCase();
    if (['paid', 'pago', 'sim', 'true', '1'].includes(v)) return 'paid';
    if (['pending', 'pendente', '', 'não', 'nao', 'no', 'false', '0'].includes(v)) return 'pending';
    return v || 'pending';
  };

  // Helper para obter faturas filtradas e ordenadas por nosso_numero
  const getFilteredInvoices = () => {
    let filtered = invoices;
    if (selectedPartner) {
      if (selectedPartner === '__sem_parceiro__') {
        // Filtrar faturas sem parceiro - mesma lógica do endpoint sync-partners
        filtered = filtered.filter(invoice => 
          !invoice.partner_name || 
          invoice.partner_name.trim() === '' ||
          !invoice.partner_id ||
          invoice.partner_id === 'None' ||
          invoice.partner_id.trim() === ''
        );
      } else {
        filtered = filtered.filter(invoice => invoice.partner_name === selectedPartner);
      }
    }
    if (searchTerm) {
      filtered = filtered.filter(invoice =>
        invoice.cooperative_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.installation_number?.toString().includes(searchTerm)
      );
    }
    if (selectedInstallation) {
      filtered = filtered.filter(invoice => (invoice.installation_number || '').trim() === selectedInstallation);
    }
    if (selectedMonth) {
      filtered = filtered.filter(invoice => (invoice.reference_month || '').trim().toUpperCase() === selectedMonth.trim().toUpperCase());
    }
    if (selectedStatus) {
      filtered = filtered.filter(invoice => invoice.payment_status === selectedStatus);
    }
    if (selectedCommissionStatus) {
      filtered = filtered.filter(invoice => normalizeStatus(invoice.commission_status) === selectedCommissionStatus);
    }
    if (dueDateStart) {
      filtered = filtered.filter(invoice => {
        const d = (invoice.payment_date || '').slice(0, 10);
        return d && d >= dueDateStart;
      });
    }
    if (dueDateEnd) {
      filtered = filtered.filter(invoice => {
        const d = (invoice.payment_date || '').slice(0, 10);
        return d && d <= dueDateEnd;
      });
    }
    // Ordenar por nosso_numero em ordem crescente
    filtered = filtered.sort((a, b) => {
      const numA = parseInt(a.nosso_numero || a.our_number || '0', 10);
      const numB = parseInt(b.nosso_numero || b.our_number || '0', 10);
      return numA - numB;
    });
    return filtered;
  };

  if (loading && !hasLoaded) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Carregando faturas...</p>
        </div>
      </Layout>
    );
  }

  const filteredInvoicesForSelection = getFilteredInvoices();
  
  // Calcular faturas da página atual para seleção
  const paginatedInvoicesForSelection = filteredInvoicesForSelection; // já paginado no servidor

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Gestão de Faturas
            </h1>
            {user?.role === 'admin' && lastImport?.timestamp && (
              <p
                className="text-xs text-gray-600 mt-1 flex items-center gap-1"
                data-testid="last-csv-import-info"
                title={lastImport.filename ? `Arquivo: ${lastImport.filename}` : ''}
              >
                <Upload className="w-3 h-3 text-emerald-600" />
                Última importação CSV:{" "}
                <span className="font-semibold text-gray-800">
                  {new Date(lastImport.timestamp).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                </span>
                {lastImport.imported_count > 0 && (
                  <span className="text-gray-500">• {lastImport.imported_count} fatura(s) criada(s)</span>
                )}
                {lastImport.imported_by_name && (
                  <span className="text-gray-500">• por {lastImport.imported_by_name}</span>
                )}
              </p>
            )}
            {user?.role === 'admin' && lastImport && !lastImport.timestamp && (
              <p className="text-xs text-gray-500 mt-1" data-testid="last-csv-import-info">
                Última importação CSV: nenhuma registrada ainda.
              </p>
            )}
          </div>
          
          {user?.role === 'admin' && (
            <div className="flex gap-2 flex-wrap">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleImportCSV}
                className="hidden"
              />
              <input
                ref={updateFileInputRef}
                type="file"
                accept=".csv"
                onChange={handleUpdateCSV}
                className="hidden"
              />
              <Button
                onClick={handleDownloadTemplate}
                className="bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Baixar Modelo
              </Button>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                {importing ? "Importando..." : "Importar CSV"}
              </Button>
              <Button
                onClick={handleDownloadUpdateTemplate}
                className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                title="Baixar modelo para atualização de status"
              >
                <Download className="w-4 h-4 mr-2" />
                Modelo Atualização
              </Button>
              <Button
                onClick={() => updateFileInputRef.current?.click()}
                disabled={importing}
                className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                title="Atualizar Status e Data de Pagamento via CSV"
              >
                <Edit className="w-4 h-4 mr-2" />
                {importing ? "Atualizando..." : "Atualizar CSV"}
              </Button>
              {importId && (
                <Button
                  onClick={handleDownloadUpdateErrors}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                  title="Baixar planilha com detalhes dos erros"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Erros
                </Button>
              )}
              <Button
                onClick={handleExportCSV}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar CSV
              </Button>
              <Button
                onClick={handleRecalculateBonus}
                disabled={recalculatingBonus}
                className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700"
                title="Recalcular bônus - Aplica bônus apenas na primeira fatura de cada unidade consumidora"
                data-testid="recalculate-bonus-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${recalculatingBonus ? 'animate-spin' : ''}`} />
                {recalculatingBonus ? "Recalculando..." : "Recalcular Bônus"}
              </Button>
              <Button
                onClick={handleRecalculateCommissions}
                disabled={recalculatingCommissions}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700"
                title="Recalcular comissões - Atualiza valores com base na % atual do parceiro"
                data-testid="recalculate-commissions-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${recalculatingCommissions ? 'animate-spin' : ''}`} />
                {recalculatingCommissions ? "Recalculando..." : "Recalcular Comissão"}
              </Button>
              <Button
                onClick={handleSyncPartners}
                disabled={syncingPartners}
                className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                title="Sincronizar faturas sem parceiro - Vincula automaticamente pelo nome do cooperado"
                data-testid="sync-partners-btn"
              >
                <Link className={`w-4 h-4 mr-2 ${syncingPartners ? 'animate-spin' : ''}`} />
                {syncingPartners ? "Sincronizando..." : "Sincronizar Parceiros"}
              </Button>
              {totalCount > 0 && (
                <Button
                  onClick={handleDeleteAll}
                  className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Deletar Todas
                </Button>
              )}
            </div>
          )}
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Lista de Faturas ({totalCount.toLocaleString('pt-BR')}){loading && <span className="text-xs font-normal text-gray-400 ml-2">atualizando…</span>}
            </CardTitle>
            
            {/* Filtros */}
            <div className="mt-4 flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <Search className="w-5 h-5 text-gray-400" />
                <Input
                  placeholder="Buscar por cooperado ou número de instalação..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="max-w-md"
                />
              </div>
              
              <div className="flex items-center gap-4 flex-wrap">
                {user?.role === 'admin' && (
                  <>
                    <label className="text-sm font-medium text-gray-700">Parceiro:</label>
                    <select
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      value={selectedPartner}
                      onChange={(e) => {
                        setSelectedPartner(e.target.value);
                        setSelectedInstallation(""); // reset ao trocar de parceiro
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Todos os parceiros</option>
                      <option value="__sem_parceiro__">⚠️ Sem Parceiro</option>
                      {partners
                        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }))
                        .map(p => (
                          <option key={p.id} value={p.name}>{p.name}</option>
                        ))}
                    </select>
                    
                    {selectedPartner && (
                      <>
                        <label className="text-sm font-medium text-gray-700">Nº Instalação:</label>
                        <select
                          data-testid="invoices-installation-filter"
                          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          value={selectedInstallation}
                          onChange={(e) => {
                            setSelectedInstallation(e.target.value);
                            setCurrentPage(1);
                          }}
                        >
                          <option value="">Todos</option>
                          {Array.from(new Set(
                            invoices
                              .filter(i => {
                                if (selectedPartner === '__sem_parceiro__') return !i.partner_name || i.partner_name.trim() === '';
                                return i.partner_name === selectedPartner;
                              })
                              .map(i => (i.installation_number || '').trim())
                              .filter(Boolean)
                          ))
                            .sort()
                            .map(inst => (
                              <option key={inst} value={inst}>{inst}</option>
                            ))}
                        </select>
                      </>
                    )}
                  </>
                )}
                
                <label className="text-sm font-medium text-gray-700">Mês de Referência:</label>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={selectedMonth}
                  onChange={(e) => {
                    setSelectedMonth(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Todos os meses</option>
                  {availableMonths.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
                
                <label className="text-sm font-medium text-gray-700">Status de Pagamento:</label>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={selectedStatus}
                  onChange={(e) => {
                    setSelectedStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Todos os status</option>
                  <option value="paid">Pago</option>
                  <option value="pending">Pendente</option>
                  <option value="overdue">Vencido</option>
                  <option value="defaulted">Negativado</option>
                  <option value="installment">Parcelado</option>
                  <option value="canceled">Cancelado</option>
                </select>

                <label className="text-sm font-medium text-gray-700">Status de Comissão:</label>
                <select
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={selectedCommissionStatus}
                  onChange={(e) => {
                    setSelectedCommissionStatus(e.target.value);
                    setCurrentPage(1);
                  }}
                  data-testid="filter-commission-status"
                >
                  <option value="">Todos os status</option>
                  <option value="pending">Pendente</option>
                  <option value="paid">Pago</option>
                </select>

                <label className="text-sm font-medium text-gray-700">Pagamento de:</label>
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={dueDateStart}
                  onChange={(e) => { setDueDateStart(e.target.value); setCurrentPage(1); }}
                  data-testid="due-date-start"
                />
                <label className="text-sm font-medium text-gray-700">até:</label>
                <input
                  type="date"
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  value={dueDateEnd}
                  onChange={(e) => { setDueDateEnd(e.target.value); setCurrentPage(1); }}
                  data-testid="due-date-end"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Barra de Ações em Lote */}
            {canEdit() && selectedInvoices.size > 0 && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-800">
                    {selectedInvoices.size} fatura(s) selecionada(s)
                  </span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={() => openBulkActionDialog('payment')}
                    className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                    size="sm"
                  >
                    Atualizar Pagamento
                  </Button>
                  <Button
                    onClick={() => openBulkActionDialog('commission')}
                    className="bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700"
                    size="sm"
                  >
                    Atualizar Comissão
                  </Button>
                  <Button
                    onClick={() => openBulkActionDialog('bonus')}
                    className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
                    size="sm"
                  >
                    Atualizar Bônus
                  </Button>
                  <Button
                    onClick={handleDeleteSelected}
                    className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Deletar Selecionadas
                  </Button>
                  <Button
                    onClick={() => setSelectedInvoices(new Set())}
                    variant="outline"
                    size="sm"
                  >
                    Limpar Seleção
                  </Button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {canEdit() ? (
                      // Admin e Operador veem TODAS as colunas
                      <>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={paginatedInvoicesForSelection.length > 0 && paginatedInvoicesForSelection.every(inv => selectedInvoices.has(inv.id))}
                            onCheckedChange={() => handleSelectAll(paginatedInvoicesForSelection)}
                            aria-label="Selecionar todas da página"
                          />
                        </TableHead>
                        <SortableHeader column="cooperative_name">Nome do Cooperado</SortableHeader>
                        <SortableHeader column="installation_number">Nº Instalação</SortableHeader>
                        <SortableHeader column="distributor">Distribuidora</SortableHeader>
                        <SortableHeader column="partner_name">Parceiro</SortableHeader>
                        <SortableHeader column="generator_installation_number">Nº Instalação Gerador</SortableHeader>
                        <SortableHeader column="reference_month">Mês Referência</SortableHeader>
                        <SortableHeader column="invoice_amount">Valor Fatura</SortableHeader>
                        <SortableHeader column="nosso_numero">Nosso Número</SortableHeader>
                        <SortableHeader column="due_date">Vencimento Assinatura</SortableHeader>
                        <SortableHeader column="payment_status">Status Pagamento</SortableHeader>
                        <SortableHeader column="credits_fp">Créditos FP</SortableHeader>
                        <SortableHeader column="signature_date">Data Assinatura</SortableHeader>
                        <SortableHeader column="commission_percentage">% Comissão</SortableHeader>
                        <SortableHeader column="commission_amount">Valor Comissão</SortableHeader>
                        <SortableHeader column="economy">Economia</SortableHeader>
                        <SortableHeader column="payment_date">Data Pagamento</SortableHeader>
                        <SortableHeader column="commission_status">Status Comissão</SortableHeader>
                        <SortableHeader column="bonus">Bônus</SortableHeader>
                        <SortableHeader column="bonus_status">Status Bônus</SortableHeader>
                        <SortableHeader column="compensated_energy_kwh">Energia Compensada (kWh)</SortableHeader>
                        <TableHead>Ações</TableHead>
                      </>
                    ) : (
                      // Parceiro vê apenas colunas básicas
                      <>
                        <SortableHeader column="cooperative_name">Nome do Cooperado</SortableHeader>
                        <SortableHeader column="installation_number">Número de Instalação</SortableHeader>
                        <SortableHeader column="invoice_amount">Valor da Fatura</SortableHeader>
                        <SortableHeader column="economy">Economia</SortableHeader>
                        <SortableHeader column="due_date">Data de Vencimento</SortableHeader>
                        <SortableHeader column="payment_date">Data de Pagamento</SortableHeader>
                        <SortableHeader column="payment_status">Status do Pagamento</SortableHeader>
                      </>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // Apply filters - use the same logic as getFilteredInvoices()
                    let filteredInvoices = invoices;
                    
                    // Partner filter (admin only) - FIXED: handle "__sem_parceiro__" case
                    if (selectedPartner) {
                      if (selectedPartner === '__sem_parceiro__') {
                        filteredInvoices = filteredInvoices.filter(invoice => 
                          !invoice.partner_name || 
                          invoice.partner_name.trim() === '' ||
                          !invoice.partner_id ||
                          invoice.partner_id === 'None' ||
                          invoice.partner_id.trim() === ''
                        );
                      } else {
                        filteredInvoices = filteredInvoices.filter(invoice =>
                          invoice.partner_name === selectedPartner
                        );
                      }
                    }
                    
                    // Search filter
                    if (searchTerm) {
                      filteredInvoices = filteredInvoices.filter(invoice =>
                        invoice.cooperative_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        invoice.installation_number?.toString().includes(searchTerm)
                      );
                    }
                    
                    // Installation filter (admin only)
                    if (selectedInstallation) {
                      filteredInvoices = filteredInvoices.filter(invoice =>
                        (invoice.installation_number || '').trim() === selectedInstallation
                      );
                    }
                    
                    // Month filter
                    if (selectedMonth) {
                      filteredInvoices = filteredInvoices.filter(invoice =>
                        invoice.reference_month === selectedMonth
                      );
                    }
                    
                    // Status filter
                    if (selectedStatus) {
                      filteredInvoices = filteredInvoices.filter(invoice =>
                        invoice.payment_status === selectedStatus
                      );
                    }
                    if (selectedCommissionStatus) {
                      filteredInvoices = filteredInvoices.filter(invoice =>
                        normalizeStatus(invoice.commission_status) === selectedCommissionStatus
                      );
                    }
                    // Due date filter
                    if (dueDateStart) {
                      filteredInvoices = filteredInvoices.filter(invoice => {
                        const d = (invoice.payment_date || '').slice(0, 10);
                        return d && d >= dueDateStart;
                      });
                    }
                    if (dueDateEnd) {
                      filteredInvoices = filteredInvoices.filter(invoice => {
                        const d = (invoice.payment_date || '').slice(0, 10);
                        return d && d <= dueDateEnd;
                      });
                    }
                    
                    // Ordenação
                    if (sortColumn) {
                      filteredInvoices = [...filteredInvoices].sort((a, b) => {
                        let aVal = a[sortColumn];
                        let bVal = b[sortColumn];
                        
                        // Tratar valores nulos
                        if (aVal === null || aVal === undefined) aVal = '';
                        if (bVal === null || bVal === undefined) bVal = '';
                        
                        // Ordenação numérica para campos numéricos
                        const numericFields = ['nosso_numero', 'invoice_amount', 'bonus', 'commission_percentage', 'commission_amount', 'economy', 'compensated_energy_kwh'];
                        if (numericFields.includes(sortColumn)) {
                          aVal = parseFloat(aVal) || 0;
                          bVal = parseFloat(bVal) || 0;
                        }
                        
                        // Ordenação de datas
                        const dateFields = ['due_date', 'payment_date', 'reference_month', 'signature_date'];
                        if (dateFields.includes(sortColumn)) {
                          aVal = aVal ? new Date(aVal).getTime() : 0;
                          bVal = bVal ? new Date(bVal).getTime() : 0;
                        }
                        
                        // Comparação
                        if (typeof aVal === 'string') {
                          aVal = aVal.toLowerCase();
                          bVal = bVal.toLowerCase();
                        }
                        
                        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
                        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
                        return 0;
                      });
                    }
                    
                    // Paginação já feita no servidor
                    const paginatedInvoices = filteredInvoices;
                    
                    if (filteredInvoices.length === 0) {
                      return (
                        <TableRow>
                          <TableCell colSpan={canEdit() ? 22 : 7} className="text-center text-gray-500 py-8">
                            {totalCount === 0 && !selectedMonth && !selectedPartner && !debouncedSearch && !selectedStatus && !selectedCommissionStatus && !selectedInstallation && !dueDateStart && !dueDateEnd
                              ? "Nenhuma fatura cadastrada. Importe um arquivo CSV para começar."
                              : "Nenhuma fatura encontrada com os filtros aplicados."
                            }
                          </TableCell>
                        </TableRow>
                      );
                    }
                    
                    return paginatedInvoices.map((invoice) => (
                      <TableRow key={invoice.id} className={selectedInvoices.has(invoice.id) ? "bg-blue-50" : ""}>
                        {canEdit() ? (
                          // Admin e Operador veem TODAS as colunas
                          <>
                            <TableCell>
                              <Checkbox
                                checked={selectedInvoices.has(invoice.id)}
                                onCheckedChange={() => handleSelectInvoice(invoice.id)}
                                aria-label={`Selecionar fatura ${invoice.id}`}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{invoice.cooperative_name}</TableCell>
                            <TableCell>{invoice.installation_number}</TableCell>
                            <TableCell>{(invoice.distributor || '-').toString().trim().toUpperCase()}</TableCell>
                            <TableCell>{invoice.partner_name || '-'}</TableCell>
                            <TableCell>{invoice.generator_installation_number || '-'}</TableCell>
                            <TableCell>{(invoice.reference_month || '-').toString().trim().toUpperCase()}</TableCell>
                            <TableCell className="font-semibold">
                              {canEdit() && editingField.id === invoice.id && editingField.field === 'invoice_amount' ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={editingField.value}
                                    onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveInlineField(invoice.id, 'invoice_amount');
                                      if (e.key === 'Escape') cancelEditingField();
                                    }}
                                    className="w-24 h-8 text-sm"
                                    autoFocus
                                    data-testid={`invoice-amount-input-${invoice.id}`}
                                  />
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-green-600 hover:bg-green-50" onClick={() => handleSaveInlineField(invoice.id, 'invoice_amount')} title="Salvar">✓</Button>
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={cancelEditingField} title="Cancelar">✕</Button>
                                </div>
                              ) : canEdit() ? (
                                <span
                                  className="cursor-pointer hover:bg-emerald-50 px-2 py-1 rounded transition-colors"
                                  onClick={() => startEditingField(invoice, 'invoice_amount')}
                                  title="Clique para editar o valor da fatura"
                                  data-testid={`invoice-amount-value-${invoice.id}`}
                                >
                                  {formatCurrency(invoice.invoice_amount)}
                                </span>
                              ) : (
                                formatCurrency(invoice.invoice_amount)
                              )}
                            </TableCell>
                            <TableCell className="font-medium text-blue-600">{invoice.nosso_numero || invoice.our_number || '-'}</TableCell>
                            <TableCell className="text-sm">{invoice.signature_due_date ? formatDate(invoice.signature_due_date) : '-'}</TableCell>
                            <TableCell>
                              <select
                                className={`px-2 py-1 rounded-md text-xs font-semibold border-0 cursor-pointer ${
                                  invoice.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 
                                  invoice.payment_status === 'overdue' ? 'bg-orange-100 text-orange-700' :
                                  invoice.payment_status === 'defaulted' ? 'bg-red-100 text-red-700' :
                                  invoice.payment_status === 'installment' ? 'bg-blue-100 text-blue-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}
                                value={invoice.payment_status}
                                onChange={(e) => handleUpdatePaymentStatus(invoice.id, e.target.value)}
                              >
                                <option value="pending">Pendente</option>
                                <option value="paid">Pago</option>
                                <option value="overdue">Vencido</option>
                                <option value="defaulted">Negativado</option>
                                <option value="installment">Parcelado</option>
                              </select>
                            </TableCell>
                            <TableCell>{invoice.fp_credits?.toLocaleString('pt-BR') || '0'}</TableCell>
                            <TableCell className="text-sm">{invoice.signature_date ? formatDate(invoice.signature_date) : '-'}</TableCell>
                            <TableCell>{invoice.commission_percentage?.toFixed(2)}%</TableCell>
                            <TableCell className="font-semibold">
                              {canEdit() && editingField.id === invoice.id && editingField.field === 'commission_amount' ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    step="0.01"
                                    value={editingField.value}
                                    onChange={(e) => setEditingField({ ...editingField, value: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handleSaveInlineField(invoice.id, 'commission_amount');
                                      if (e.key === 'Escape') cancelEditingField();
                                    }}
                                    className="w-24 h-8 text-sm"
                                    autoFocus
                                    data-testid={`commission-amount-input-${invoice.id}`}
                                  />
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-green-600 hover:bg-green-50" onClick={() => handleSaveInlineField(invoice.id, 'commission_amount')} title="Salvar">✓</Button>
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50" onClick={cancelEditingField} title="Cancelar">✕</Button>
                                </div>
                              ) : canEdit() ? (
                                <span
                                  className="cursor-pointer hover:bg-emerald-50 px-2 py-1 rounded transition-colors"
                                  onClick={() => startEditingField(invoice, 'commission_amount')}
                                  title="Clique para editar o valor da comissão"
                                  data-testid={`commission-amount-value-${invoice.id}`}
                                >
                                  {formatCurrency(invoice.commission_amount || 0)}
                                </span>
                              ) : (
                                formatCurrency(invoice.commission_amount || 0)
                              )}
                            </TableCell>
                            <TableCell className="text-green-600 font-semibold">{formatCurrency(invoice.economy || 0)}</TableCell>
                            <TableCell className="text-sm">{invoice.payment_date ? formatDate(invoice.payment_date) : '-'}</TableCell>
                            <TableCell>
                              {canEdit() ? (
                                <Select 
                                  value={normalizeStatus(invoice.commission_status)} 
                                  onValueChange={(value) => handleUpdateCommissionStatus(invoice.id, value)}
                                >
                                  <SelectTrigger className="w-[120px]">
                                    <SelectValue>
                                      {normalizeStatus(invoice.commission_status) === 'paid' && (
                                        <span className="flex items-center gap-2">
                                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                          Pago
                                        </span>
                                      )}
                                      {normalizeStatus(invoice.commission_status) === 'pending' && (
                                        <span className="flex items-center gap-2">
                                          <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                          Pendente
                                        </span>
                                      )}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">Pendente</SelectItem>
                                    <SelectItem value="paid">Pago</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className={`px-2 py-1 rounded-md text-xs font-semibold ${
                                  normalizeStatus(invoice.commission_status) === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {normalizeStatus(invoice.commission_status) === 'paid' ? 'Pago' : 'Pendente'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="font-semibold">
                              {canEdit() ? (
                                editingBonusId === invoice.id ? (
                                  <div className="flex items-center gap-1">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={editingBonusValue}
                                      onChange={(e) => setEditingBonusValue(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleSaveBonusInline(invoice.id);
                                        if (e.key === 'Escape') cancelEditingBonus();
                                      }}
                                      className="w-24 h-8 text-sm"
                                      autoFocus
                                      data-testid={`bonus-input-${invoice.id}`}
                                    />
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                                      onClick={() => handleSaveBonusInline(invoice.id)}
                                      title="Salvar"
                                    >
                                      ✓
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={cancelEditingBonus}
                                      title="Cancelar"
                                    >
                                      ✕
                                    </Button>
                                  </div>
                                ) : (
                                  <span 
                                    className="cursor-pointer hover:bg-amber-50 px-2 py-1 rounded transition-colors"
                                    onClick={() => startEditingBonus(invoice)}
                                    title="Clique para editar o bônus"
                                    data-testid={`bonus-value-${invoice.id}`}
                                  >
                                    {formatCurrency(invoice.bonus || 0)}
                                  </span>
                                )
                              ) : (
                                formatCurrency(invoice.bonus || 0)
                              )}
                            </TableCell>
                            <TableCell>
                              {canEdit() ? (
                                <Select 
                                  value={normalizeStatus(invoice.bonus_status)} 
                                  onValueChange={(value) => handleUpdateBonusStatus(invoice.id, value)}
                                >
                                  <SelectTrigger className="w-[120px]">
                                    <SelectValue>
                                      {normalizeStatus(invoice.bonus_status) === 'paid' && (
                                        <span className="flex items-center gap-2">
                                          <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                          Pago
                                        </span>
                                      )}
                                      {normalizeStatus(invoice.bonus_status) === 'pending' && (
                                        <span className="flex items-center gap-2">
                                          <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                          Pendente
                                        </span>
                                      )}
                                    </SelectValue>
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">Pendente</SelectItem>
                                    <SelectItem value="paid">Pago</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <span className={`px-2 py-1 rounded-md text-xs font-semibold ${
                                  normalizeStatus(invoice.bonus_status) === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {normalizeStatus(invoice.bonus_status) === 'paid' ? 'Pago' : 'Pendente'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>{invoice.compensated_energy?.toLocaleString('pt-BR') || '0'}</TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingInvoice(invoice);
                                    setEditFormData({
                                      invoice_amount: invoice.invoice_amount || 0,
                                      commission_percentage: invoice.commission_percentage || 0,
                                      commission_amount: invoice.commission_amount || 0,
                                      bonus: invoice.bonus || 0,
                                      fp_credits: invoice.fp_credits || 0,
                                      economy: invoice.economy || 0,
                                      compensated_energy: invoice.compensated_energy || 0,
                                      generator_installation_number: invoice.generator_installation_number || '',
                                      partner_name: invoice.partner_name || '',
                                      payment_status: invoice.payment_status || 'pending',
                                      commission_status: normalizeStatus(invoice.commission_status),
                                      bonus_status: normalizeStatus(invoice.bonus_status)
                                    });
                                    setEditDialogOpen(true);
                                  }}
                                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                  title="Editar"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(invoice.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  title="Excluir"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </>
                        ) : (
                          // Parceiro vê apenas colunas básicas
                          <>
                            <TableCell className="font-medium">{invoice.cooperative_name}</TableCell>
                            <TableCell>{invoice.installation_number}</TableCell>
                            <TableCell className="font-semibold">{formatCurrency(invoice.invoice_amount)}</TableCell>
                            <TableCell className="text-green-600 font-semibold">{formatCurrency(invoice.economy || 0)}</TableCell>
                            <TableCell className="text-sm">{invoice.signature_due_date ? formatDate(invoice.signature_due_date) : '-'}</TableCell>
                            <TableCell className="text-sm">{invoice.payment_date ? formatDate(invoice.payment_date) : '-'}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-md text-xs font-semibold ${
                                invoice.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 
                                invoice.payment_status === 'overdue' ? 'bg-orange-100 text-orange-700' :
                                invoice.payment_status === 'defaulted' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }`}>
                                {invoice.payment_status === 'paid' ? 'Pago' : 
                                 invoice.payment_status === 'overdue' ? 'Vencido' :
                                 invoice.payment_status === 'defaulted' ? 'Negativado' : 'Aguardando'}
                              </span>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </div>
            
            {/* Pagination Controls */}
            {(() => {
              // Calculate filtered invoices for pagination - use same logic as table
              let filteredInvoices = invoices;
              
              // Partner filter - handle "__sem_parceiro__" case
              if (selectedPartner) {
                if (selectedPartner === '__sem_parceiro__') {
                  filteredInvoices = filteredInvoices.filter(invoice => 
                    !invoice.partner_name || 
                    invoice.partner_name.trim() === '' ||
                    !invoice.partner_id ||
                    invoice.partner_id === 'None' ||
                    invoice.partner_id.trim() === ''
                  );
                } else {
                  filteredInvoices = filteredInvoices.filter(invoice =>
                    invoice.partner_name === selectedPartner
                  );
                }
              }
              
              if (searchTerm) {
                filteredInvoices = filteredInvoices.filter(invoice =>
                  invoice.cooperative_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  invoice.installation_number?.toString().includes(searchTerm)
                );
              }
              
              if (selectedInstallation) {
                filteredInvoices = filteredInvoices.filter(invoice =>
                  (invoice.installation_number || '').trim() === selectedInstallation
                );
              }
              
              if (selectedMonth) {
                filteredInvoices = filteredInvoices.filter(invoice =>
                  invoice.reference_month === selectedMonth
                );
              }
              
              if (selectedStatus) {
                filteredInvoices = filteredInvoices.filter(invoice =>
                  invoice.payment_status === selectedStatus
                );
              }
              if (selectedCommissionStatus) {
                filteredInvoices = filteredInvoices.filter(invoice =>
                  normalizeStatus(invoice.commission_status) === selectedCommissionStatus
                );
              }
              if (dueDateStart) {
                filteredInvoices = filteredInvoices.filter(invoice => {
                  const d = (invoice.payment_date || '').slice(0, 10);
                  return d && d >= dueDateStart;
                });
              }
              if (dueDateEnd) {
                filteredInvoices = filteredInvoices.filter(invoice => {
                  const d = (invoice.payment_date || '').slice(0, 10);
                  return d && d <= dueDateEnd;
                });
              }
              
              const totalPages = Math.ceil(totalCount / itemsPerPage);
              
              return (
                <div className="flex items-center justify-between px-6 py-4 border-t">
                  {/* Items per page selector */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Itens por página:</span>
                    <Select value={itemsPerPage.toString()} onValueChange={(v) => { setItemsPerPage(Number(v)); setCurrentPage(1); }}>
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-gray-500">
                      Mostrando {totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, totalCount)} de {totalCount.toLocaleString('pt-BR')} faturas
                    </span>
                  </div>
                  
                  {/* Pagination buttons */}
                  {totalPages > 1 && (
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        variant="outline"
                        size="sm"
                      >
                        Anterior
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => {
                            // Show first page, last page, current page, and pages around current
                            return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                          })
                          .map((page, index, array) => {
                            // Add ellipsis if there's a gap
                            const prevPage = array[index - 1];
                            const showEllipsis = prevPage && page - prevPage > 1;
                            
                            return (
                              <div key={page} className="flex items-center">
                                {showEllipsis && <span className="px-2 text-gray-400">...</span>}
                                <Button
                                  onClick={() => setCurrentPage(page)}
                                  variant={currentPage === page ? "default" : "outline"}
                                  size="sm"
                                  className={currentPage === page ? "bg-blue-600" : ""}
                                >
                                  {page}
                                </Button>
                              </div>
                            );
                          })}
                      </div>
                      <Button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage >= totalPages}
                        variant="outline"
                        size="sm"
                      >
                        Próximo
                      </Button>
                    </div>
                  )}
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Edição */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Fatura</DialogTitle>
            <DialogDescription>
              Edite os campos da fatura conforme necessário.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="invoice_amount">Valor da Fatura (R$)</Label>
                <Input
                  id="invoice_amount"
                  type="number"
                  step="0.01"
                  value={editFormData.invoice_amount}
                  onChange={(e) => setEditFormData({...editFormData, invoice_amount: parseFloat(e.target.value)})}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="commission_percentage">% Comissão</Label>
                <Input
                  id="commission_percentage"
                  type="number"
                  step="0.01"
                  value={editFormData.commission_percentage}
                  onChange={(e) => setEditFormData({...editFormData, commission_percentage: parseFloat(e.target.value)})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="commission_amount">Valor Comissão (R$)</Label>
                <Input
                  id="commission_amount"
                  type="number"
                  step="0.01"
                  value={editFormData.commission_amount}
                  onChange={(e) => setEditFormData({...editFormData, commission_amount: parseFloat(e.target.value)})}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bonus">Bônus (R$)</Label>
                <Input
                  id="bonus"
                  type="number"
                  step="0.01"
                  value={editFormData.bonus}
                  onChange={(e) => setEditFormData({...editFormData, bonus: parseFloat(e.target.value)})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="fp_credits">Créditos FP</Label>
                <Input
                  id="fp_credits"
                  type="number"
                  step="0.01"
                  value={editFormData.fp_credits}
                  onChange={(e) => setEditFormData({...editFormData, fp_credits: parseFloat(e.target.value)})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="economy">Economia (R$)</Label>
                <Input
                  id="economy"
                  type="number"
                  step="0.01"
                  value={editFormData.economy}
                  onChange={(e) => setEditFormData({...editFormData, economy: parseFloat(e.target.value)})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="compensated_energy">Energia Compensada (kWh)</Label>
                <Input
                  id="compensated_energy"
                  type="number"
                  step="0.01"
                  value={editFormData.compensated_energy}
                  onChange={(e) => setEditFormData({...editFormData, compensated_energy: parseFloat(e.target.value)})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="generator_installation_number">Número de Instalação do Gerador</Label>
                <Input
                  id="generator_installation_number"
                  type="text"
                  value={editFormData.generator_installation_number}
                  onChange={(e) => setEditFormData({...editFormData, generator_installation_number: e.target.value})}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="partner_name">Parceiro</Label>
                <Select
                  value={editFormData.partner_name}
                  onValueChange={(value) => setEditFormData({...editFormData, partner_name: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o parceiro" />
                  </SelectTrigger>
                  <SelectContent>
                    {partners.map(partner => (
                      <SelectItem key={partner.id} value={partner.name}>
                        {partner.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="payment_status">Status de Pagamento</Label>
                <Select
                  value={editFormData.payment_status}
                  onValueChange={(value) => setEditFormData({...editFormData, payment_status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="paid">Pago</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="commission_status">Status da Comissão</Label>
                <Select
                  value={editFormData.commission_status}
                  onValueChange={(value) => setEditFormData({...editFormData, commission_status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="paid">Pago</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="bonus_status">Status do Bônus</Label>
                <Select
                  value={editFormData.bonus_status}
                  onValueChange={(value) => setEditFormData({...editFormData, bonus_status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="paid">Pago</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                Salvar Alterações
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Ação em Lote */}
      <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {bulkActionType === 'payment' && 'Atualizar Status de Pagamento'}
              {bulkActionType === 'commission' && 'Atualizar Status de Comissão'}
              {bulkActionType === 'bonus' && 'Atualizar Status de Bônus'}
            </DialogTitle>
            <DialogDescription>
              Esta ação será aplicada a <strong>{selectedInvoices.size}</strong> fatura(s) selecionada(s).
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="bulk-status" className="mb-2 block">
              Selecione o novo status:
            </Label>
            <Select value={bulkActionStatus} onValueChange={setBulkActionStatus}>
              <SelectTrigger id="bulk-status">
                <SelectValue placeholder="Selecione o status" />
              </SelectTrigger>
              <SelectContent>
                {bulkActionType === 'payment' ? (
                  <>
                    <SelectItem value="pending">Aguardando</SelectItem>
                    <SelectItem value="paid">Pago</SelectItem>
                    <SelectItem value="overdue">Vencido</SelectItem>
                    <SelectItem value="defaulted">Negativado</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="paid">Pago</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setBulkActionDialogOpen(false)}
              disabled={bulkUpdating}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleBulkUpdate}
              disabled={!bulkActionStatus || bulkUpdating}
              className={
                bulkActionType === 'payment' ? 'bg-gradient-to-r from-green-500 to-emerald-600' :
                bulkActionType === 'commission' ? 'bg-gradient-to-r from-purple-500 to-violet-600' :
                'bg-gradient-to-r from-amber-500 to-orange-600'
              }
            >
              {bulkUpdating ? 'Atualizando...' : `Atualizar ${selectedInvoices.size} fatura(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
